import { DeleteIcon, HelpCircleIcon } from '@approvalmax/ui';
import {
    Alert,
    Button,
    Checkbox,
    Flex,
    Form,
    Grid,
    Link,
    Popup,
    Progress,
    Text,
    useConfirmation,
} from '@approvalmax/ui/src/components';
import { miscHelpers } from '@approvalmax/utils';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
    ApiAmaxPayPaymentDetailsVerificationStatus,
    PaymentDetailsLegalEntityType,
    useDeletePaymentDetails,
    useGetPaymentDetailsRequirements,
} from 'shared/data/webApp/v2';
import { useCompanyBetaFeatures, useCompanyIdBasedOnUrl } from 'shared/hooks';

import { CheckConfirmationOfPayee } from '../CheckConfirmationOfPayee/CheckConfirmationOfPayee';
import {
    ContactInfo,
    FirstStep,
    IncorrectInfoPopupContent,
    IncorrectNamePopupContent,
    SecondStep,
    ThirdStep,
} from './components';
import { kbLink } from './EditCounterpartyPaymentDetailsPopupContent.constants';
import {
    useConfirmationOfPayee,
    useContactFromQuery,
    useFormSteps,
    useFormSubmit,
    useInitialFormValues,
} from './EditCounterpartyPaymentDetailsPopupContent.hooks';
import { messages } from './EditCounterpartyPaymentDetailsPopupContent.messages';
import {
    EditCounterpartyPaymentDetailsPopupContentProps,
    FormValues,
} from './EditCounterpartyPaymentDetailsPopupContent.types';

export const EditCounterpartyPaymentDetailsPopupContent = memo<EditCounterpartyPaymentDetailsPopupContentProps>(
    (props) => {
        const {
            contact: contactFromProps,
            contactQueryData,
            paymentDetailsId,
            paymentDetails,
            onPaymentDetailsChanged,
        } = props;

        const { companyId } = useCompanyIdBasedOnUrl();
        const { isConfirmationOfPayee, isWalletsUK } = useCompanyBetaFeatures(companyId);
        const contactFromQuery = useContactFromQuery(contactQueryData, companyId);

        const contact = contactFromProps || contactFromQuery;

        const initialValues = useInitialFormValues({
            contact,
            paymentDetailsId,
            paymentDetails,
            isConfirmationOfPayeeOnly: isConfirmationOfPayee && !isWalletsUK,
        });

        const form = useForm<FormValues>({
            defaultValues: initialValues,
            mode: 'onChange',
        });

        const [currencyCode, payerBankCountry, payeeBankCountry, companyName, firstName, lastName, legalEntityType] =
            form.watch([
                'currencyCode',
                'payerBankCountry',
                'payeeBankCountry',
                'legalEntity.companyName',
                'legalEntity.firstName',
                'legalEntity.lastName',
                'legalEntity.legalEntityType',
            ]);

        const { data: paymentDetailsRequirements, isFetching: isFetchingGetPaymentDetailsRequirements } =
            useGetPaymentDetailsRequirements(
                {
                    path: {
                        companyId,
                    },
                    query: {
                        currencyCode,
                        payerBankCountry,
                        payeeBankCountry,
                    },
                },
                {
                    staleTime: Infinity,
                }
            );

        const { mutate: deletePaymentDetails } = useDeletePaymentDetails({
            onSuccess: () => {
                onPaymentDetailsChanged();
            },
        });

        const { confirmation } = useConfirmation({
            confirmMessage: messages.areYouSureToRemove,
            confirmButtonMessage: messages.submitRemove,
            cancelButtonMessage: messages.cancel,
        });

        const {
            isConfirmationOfPayeeAvailable,
            isLoading: isConfirmationOfPayeeLoading,
            data: confirmationOfPayeeData,
            notVerifiedPayee,
            checkConfirmationOfPayeeData,
            showIncorrectInfo,
            showIncorrectName,
            resetConfirmationOfPayeeStates,
        } = useConfirmationOfPayee({
            form,
            companyId,
            paymentDetailsRequirements,
        });

        const { handleSubmit, isLoading } = useFormSubmit({
            form,
            companyId,
            counterpartyId: contact?.counterpartyId,
            paymentDetailsId,
            paymentDetailsRequirements,
            checkConfirmationOfPayeeData,
            confirmationOfPayeeData,
            onPaymentDetailsChanged,
        });

        const shouldSetInitialFormValuesRef = useRef(Boolean(paymentDetailsId));

        useEffect(() => {
            /**
             * Form is splitted into several steps, changes in prev step call reset for next step,
             * what causes conflicts with initial values, when we open popup for editing existing payment details.
             * Form is dynamic and all fields can be shown only after requirements are loaded.
             * So we need to set form initial values after requirements are loaded.
             */
            if (shouldSetInitialFormValuesRef.current && paymentDetailsRequirements && paymentDetailsId) {
                if (initialValues.bankAccount) {
                    form.setValue('bankAccount', initialValues.bankAccount);
                }

                if (initialValues.legalEntity) {
                    form.setValue('legalEntity', initialValues.legalEntity);
                }

                shouldSetInitialFormValuesRef.current = false;
            }
        }, [form, paymentDetailsRequirements, paymentDetailsId, initialValues]);

        const handleRemoveSubmit = useCallback(async () => {
            if (!contact || !paymentDetailsId) {
                return;
            }

            confirmation()
                .then(() => {
                    deletePaymentDetails({
                        params: {
                            path: {
                                companyId: companyId,
                                counterpartyId: contact.counterpartyId,
                                paymentDetailsId,
                            },
                        },
                    });
                })
                .catch(() => {});
        }, [deletePaymentDetails, confirmation, paymentDetailsId, companyId, contact]);

        const { isSecondStepVisible, isThirdStepVisible } = useFormSteps(form);

        const isFirstPaymentDetails = !paymentDetailsId && (paymentDetails ?? []).length === 0;
        const noEditChanges = Boolean(paymentDetailsId && !form.formState.isDirty);
        const hasErrors = Object.values(form.formState.errors).length > 0;

        if (showIncorrectInfo) {
            return (
                <Form form={form} onSubmit={handleSubmit}>
                    <IncorrectInfoPopupContent isLoading={isLoading} onBack={resetConfirmationOfPayeeStates} />
                </Form>
            );
        }

        if (showIncorrectName) {
            return (
                <Form form={form} onSubmit={handleSubmit}>
                    <IncorrectNamePopupContent
                        suggestedName={confirmationOfPayeeData?.actualName ?? ''}
                        isLoading={isLoading}
                        onBack={resetConfirmationOfPayeeStates}
                    />
                </Form>
            );
        }

        return (
            <Form form={form} onSubmit={handleSubmit}>
                <Popup.Header
                    title={
                        paymentDetailsId
                            ? messages.titleEdit({ contact: contact?.name })
                            : messages.titleNew({ contact: contact?.name })
                    }
                    actions={
                        <Button
                            disabled={noEditChanges || notVerifiedPayee || !isThirdStepVisible || hasErrors}
                            type='submit'
                            color='blue80'
                            size='medium'
                            progress={isLoading}
                        >
                            {messages.save}
                        </Button>
                    }
                />

                <Popup.Body>
                    <Flex direction='column' spacing='24'>
                        <Alert color='midnight20' closable alignItems='center' startIcon={<HelpCircleIcon />}>
                            <Flex direction='column' spacing='4'>
                                <Text font='body' fontSize='small' color='midnight80'>
                                    {messages.helpText}
                                </Text>

                                <Link external font='label' fontSize='small' href={kbLink}>
                                    {messages.learnMore}
                                </Link>
                            </Flex>
                        </Alert>

                        {contact && <ContactInfo contact={contact} />}

                        <Flex alignItems='center' justifyContent='space-between'>
                            <Checkbox name='isDefault' disabled={isLoading}>
                                {messages.defaultCheckbox}
                            </Checkbox>

                            {isConfirmationOfPayeeAvailable && (
                                <CheckConfirmationOfPayee
                                    progress={isConfirmationOfPayeeLoading}
                                    status={miscHelpers.castToEnum(
                                        ApiAmaxPayPaymentDetailsVerificationStatus,
                                        confirmationOfPayeeData?.status
                                    )}
                                    reasonCode={confirmationOfPayeeData?.reasonCode}
                                    suggestedName={confirmationOfPayeeData?.actualName}
                                    providedName={
                                        legalEntityType === PaymentDetailsLegalEntityType.Company
                                            ? companyName
                                            : `${firstName} ${lastName}`
                                    }
                                    showInfoIcon
                                />
                            )}
                        </Flex>

                        <Grid rowGap={32}>
                            <FirstStep disabled={Boolean(paymentDetailsId)} />

                            {isFetchingGetPaymentDetailsRequirements && (
                                <Flex alignItems='center' justifyContent='center' height={60}>
                                    <Progress shape='circle' size='small' />
                                </Flex>
                            )}

                            {isSecondStepVisible && !isFetchingGetPaymentDetailsRequirements && (
                                <SecondStep
                                    disabled={Boolean(paymentDetailsId)}
                                    requirements={paymentDetailsRequirements}
                                />
                            )}

                            {isThirdStepVisible && !isFetchingGetPaymentDetailsRequirements && (
                                <ThirdStep
                                    contact={contact}
                                    requirements={paymentDetailsRequirements}
                                    isFirstPaymentDetails={isFirstPaymentDetails}
                                />
                            )}

                            {paymentDetailsId && (
                                <div>
                                    <Button
                                        color='red40'
                                        size='medium'
                                        startIcon={<DeleteIcon />}
                                        disabled={!paymentDetailsId || isLoading}
                                        onClick={handleRemoveSubmit}
                                    >
                                        {messages.remove}
                                    </Button>
                                </div>
                            )}
                        </Grid>
                    </Flex>
                </Popup.Body>
            </Form>
        );
    }
);

EditCounterpartyPaymentDetailsPopupContent.displayName = 'EditCounterpartyPaymentDetailsPopupContent';
